import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import type { User } from '../slices/authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

export interface LoginRequest { email: string; password: string; }
export interface RegisterRequest { email: string; password: string; full_name: string; }
export interface VerifySessionRequest { session_id: string; email?: string; user_id?: number; }
export interface ForgotPasswordRequest { email: string; }
export interface ResetPasswordRequest { token: string; password: string; }
export interface MessageResponse { message: string; }
export interface LoginResponse { access_token: string; token_type: string; user: User; }
export interface VerifyEmailResponse { access_token: string; token_type: string; }
export interface CheckoutRequest { existing_user: boolean; customer_email?: string; user_id?: number; }

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken;
      if (token) headers.set('authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getMe: builder.query<User, void>({
      query: () => '/auth/me',
      providesTags: ['User'],
    }),
    register: builder.mutation<MessageResponse, RegisterRequest>({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
    }),
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
    }),
    forgotPassword: builder.mutation<MessageResponse, ForgotPasswordRequest>({
      query: (body) => ({ url: '/auth/forgot-password', method: 'POST', body }),
    }),
    resetPassword: builder.mutation<MessageResponse, ResetPasswordRequest>({
      query: (body) => ({ url: '/auth/reset-password', method: 'POST', body }),
    }),
    resendVerification: builder.mutation<MessageResponse, ForgotPasswordRequest>({
      query: (body) => ({ url: '/auth/resend-verification', method: 'POST', body }),
    }),
    verifyEmail: builder.query<VerifyEmailResponse, string>({
      query: (token) => `/auth/verify-email?token=${token}`,
    }),
    verifyPaymentSession: builder.mutation<{ plan: string }, VerifySessionRequest>({
      query: (body) => ({ url: '/payments/verify-session', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    createCheckoutSession: builder.mutation<{ checkout_url: string }, CheckoutRequest>({
      query: (body) => ({ url: '/payments/checkout', method: 'POST', body }),
    }),
    createPortalSession: builder.mutation<{ portal_url: string }, void>({
      query: () => ({ url: '/payments/portal', method: 'POST' }),
    }),
    createTopupCheckout: builder.mutation<{ checkout_url: string }, void>({
      query: () => ({ url: '/payments/topup', method: 'POST' }),
    }),
  }),
});

export const {
  useGetMeQuery,
  useRegisterMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useResendVerificationMutation,
  useVerifyEmailQuery,
  useVerifyPaymentSessionMutation,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
  useCreateTopupCheckoutMutation,
} = authApi;
